import { RouteManager } from '../../../RouteManager';
import type { CommandItem, CommandProviderArgs } from '../command-provider.types';
import { COMMAND_SECTIONS } from '../command-types.ts';

import { AppUtils } from '../../../store/apps/AppUtils';
import { ResourceUtils } from '../../../store/ResourceUtils';

import { glyph } from '../../../theme/glyph';

import { isMatch, normalize } from './provider-utils.ts';

function getDeviceIconUrl(device: unknown): string | null {
  if (typeof device !== 'object' || device == null) {
    return null;
  }

  const iconUrl = (device as Record<string, unknown>).iconUrl;

  return typeof iconUrl === 'string' ? iconUrl : null;
}

function getManagerName({
  managerId,
  i18n,
}: {
  managerId: string | null;
  i18n: CommandProviderArgs['i18n'];
}): string | null {
  switch (managerId) {
    case 'logic':
      return i18n.messageFormatter('manager.logic.name');
    case 'system':
      return i18n.messageFormatter('manager.system.name');
    case 'weather':
      return i18n.messageFormatter('manager.weather.name');
    case 'apps':
      return i18n.messageFormatter('manager.apps.name');
    default:
      return null;
  }
}

function getLogOwnerName({
  log,
  devicesById,
  appsById,
  i18n,
}: {
  log: NonNullable<NonNullable<CommandProviderArgs['logsById']>[string]>;
  devicesById: CommandProviderArgs['devicesById'];
  appsById: CommandProviderArgs['appsById'];
  i18n: CommandProviderArgs['i18n'];
}): string {
  const directOwnerName = ResourceUtils.getOwnerName(log);

  if (typeof directOwnerName === 'string' && directOwnerName.trim() !== '') {
    return directOwnerName;
  }

  const { type, id } = ResourceUtils.getOwnerTypeAndIdFromOwnerUri(log?.ownerUri);

  if (type === 'device') {
    return (
      devicesById?.[id]?.name ?? id ?? i18n.messageFormatter('commandPalette.logs.missingOwner')
    );
  }

  if (type === 'app') {
    return appsById?.[id]?.name ?? id ?? i18n.messageFormatter('commandPalette.logs.missingOwner');
  }

  if (type === 'manager') {
    return (
      getManagerName({ managerId: id, i18n }) ??
      id ??
      i18n.messageFormatter('commandPalette.logs.missingOwner')
    );
  }

  return id ?? i18n.messageFormatter('commandPalette.logs.missingOwner');
}

function getLogOwnerIconMeta({
  log,
  devicesById,
  appsById,
}: {
  log: NonNullable<NonNullable<CommandProviderArgs['logsById']>[string]>;
  devicesById: CommandProviderArgs['devicesById'];
  appsById: CommandProviderArgs['appsById'];
}): Record<string, unknown> {
  const { type, id } = ResourceUtils.getOwnerTypeAndIdFromOwnerUri(log?.ownerUri);

  if (type === 'device') {
    const device = devicesById?.[id ?? ''];

    if (device == null) {
      return {};
    }

    return {
      deviceIconObj: device.iconObj ?? null,
      deviceIconUrl: getDeviceIconUrl(device),
      deviceIconOverride: device.iconOverride ?? null,
    };
  }

  if (type === 'app') {
    const app = appsById?.[id ?? ''];

    if (app == null) {
      return {};
    }

    return {
      appIconObj: app.iconObj ?? null,
      appIconBackgroundColor: AppUtils.getBackgroundColor(app),
    };
  }

  return {};
}

export function getLogCommands(args: CommandProviderArgs): CommandItem[] {
  const { query, activeContext, logsById, devicesById, appsById, i18n } = args;

  const normalizedQuery = normalize(query.trim());
  const isLogsScopeContext = activeContext?.type === 'scope' && activeContext.scopeType === 'logs';
  const openTitle = i18n.messageFormatter('commandPalette.actions.open');

  if (activeContext?.type === 'device') {
    return [];
  }

  if (activeContext?.type === 'flow') {
    return [];
  }

  if (activeContext?.type === 'app') {
    return [];
  }

  if (activeContext?.type === 'setting') {
    return [];
  }

  if (activeContext?.type === 'scope' && activeContext.scopeType !== 'logs') {
    return [];
  }

  if (normalizedQuery === '' && isLogsScopeContext !== true) {
    return [];
  }

  const scopeCommands = isLogsScopeContext
    ? [
        {
          key: 'cmd:scope:logs:open',
          kind: 'action',
          section: COMMAND_SECTIONS.NAVIGATION,
          sortOrder: 10,
          title: openTitle,
          subtitle: i18n.messageFormatter('commandPalette.actions.insightsPage'),
          icon: glyph.core.squareArrowUpRight,
          keywords: ['open', 'insights', 'logs', 'go to insights'],
          getOpenInNewTabPath() {
            return RouteManager.getPathForInsights();
          },
          onRun() {
            RouteManager.toInsights();
          },
        },
      ]
    : [];

  const commands: CommandItem[] = [...scopeCommands];
  let sortOrder = 10;

  for (const log of Object.values(logsById ?? {})) {
    if (log?.type !== 'number') {
      continue;
    }

    const ownerName = getLogOwnerName({
      log,
      devicesById,
      appsById,
      i18n,
    });

    if (
      isMatch(log?.title, normalizedQuery) !== true &&
      isMatch(ownerName, normalizedQuery) !== true &&
      isMatch(log?.id, normalizedQuery) !== true &&
      isMatch(log?.ownerUri, normalizedQuery) !== true
    ) {
      continue;
    }

    commands.push({
      key: `entity:log:${log.id}`,
      kind: 'entity',
      section: COMMAND_SECTIONS.LOGS,
      sortOrder,
      title: log?.title ?? log?.id,
      subtitle: ownerName || undefined,
      icon: glyph.system.insights,
      keywords: [log.id, ownerName, log.ownerUri].filter((value): value is string => {
        return Boolean(value);
      }),
      meta: {
        ...getLogOwnerIconMeta({
          log,
          devicesById,
          appsById,
        }),
      },
      onRun() {
        RouteManager.toInsights({
          defaultSelectedKeys: [log.id],
        });
      },
    });

    sortOrder += 1;
  }

  return commands;
}
